import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import { useAuth } from "./use-auth";

export interface ClaimLink {
    id: number;
    claimId: number;
    companyId: number;
    label: string;
    url: string;
    createdBy: number;
    createdAt: string;
}

export function useClaimLinks(claimId: number) {
    const { firebaseUser } = useAuth();
    const queryClient = useQueryClient();

    const { data: links = [], isLoading } = useQuery<ClaimLink[]>({
        queryKey: ["claim-links", claimId],
        queryFn: async () => {
            const result = await apiClient.claimLinks.getClaimLinks({ claimId });
            return result as ClaimLink[];
        },
        enabled: !!claimId && !!firebaseUser,
    });

    const createLink = useMutation({
        mutationFn: async (data: { label: string; url: string }) => {
            const result = await apiClient.claimLinks.createClaimLink({
                claimId,
                createClaimLinkRequest: data,
            });
            return result as unknown as ClaimLink;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["claim-links", claimId] });
        },
    });

    const deleteLink = useMutation({
        mutationFn: async (linkId: number) => {
            await apiClient.claimLinks.deleteClaimLink({ claimId, linkId });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["claim-links", claimId] });
        },
    });

    const updateLink = useMutation({
        mutationFn: async ({ linkId, data }: { linkId: number; data: { label?: string; url?: string } }) => {
            const result = await apiClient.claimLinks.updateClaimLink({
                claimId,
                linkId,
                updateClaimLinkRequest: data,
            });
            return result as ClaimLink;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["claim-links", claimId] });
        },
    });

    return { links, isLoading, createLink, deleteLink, updateLink };
}
